<template>
  <tabs />
  <toolbar class="toolbar">
    <filter-pane v-model:page="page" v-model="filter" :filters="filters" />
    <add-fields-pane v-model="visibleColumns" :fields="columns" />
    <action
      :icon="EditIcon"
      :label="$t('pages.campaignList.modal.editGroup.buttonText')"
      @click="openCampaignGroupModal()"
    />
    <action
      :icon="AddIcon"
      :label="$t('pages.campaignList.modal.new.buttonText')"
      @click="campaignModalOpen = true"
    />
    <export
      url="/campaigns/csv"
      :filter="totalFilter"
      :columns="columns"
      :visible-columns="visibleColumns"
      :title="csvTitle"
      :sort-key="sortKey"
      :sort-direction="sortDirection"
    />
  </toolbar>
  <campaign-group-modal
    v-model:campaign-group="editCampaignGroup"
    v-model:open="campaignGroupModalOpen"
    @edit-saved="emit('reload')"
  />
  <campaign-modal
    v-model:campaign="editCampaign"
    :campaign-group="modelValue"
    v-model:open="campaignModalOpen"
    @saved="refetch()"
  />
  <qr-modal v-model:open="qrModalOpen" :campaign-group="currentCampaignGroup" />
  <CampaignDesignFileModal v-model:open="campaignDesignFileModalOpen" :campaign="editCampaign" />
  <data-table
    class="mb-auto ml-12"
    v-model:sort-by="sortKey"
    v-model:sort-direction="sortDirection"
    :busy="totalLoading"
    :items="records"
    :columns="columns"
    :actions="actions"
    :visible-columns="visibleColumns"
  />
  <pagination v-model:page="page" v-model:per-page="perPage" class="pagination" :total="total" />
</template>

<script lang="tsx" setup>
import {computed, inject, reactive, type Ref, ref, unref} from 'vue';
import {useI18n} from 'vue-i18n';
import AddIcon from '@images/panes/add.svg?component';
import EditIcon from '@images/panes/edit.svg?component';
import FilterPane from '@components/utils/toolbar/panes/Filter.vue';
import AddFieldsPane from '@components/utils/toolbar/panes/AddFields.vue';
import Toolbar from '@components/utils/toolbar/Toolbar.vue';
import Pagination from '@components/utils/table/Pagination.vue';
import DataTable, {type Column} from '@components/utils/table/Table.vue';
import {useProvider} from '@components/utils/table/hooks/provider';
import {format} from 'date-fns';
import type {Filter} from '@/components/utils/filter/Filter.vue';
import type {Campaign, CampaignGroup, HydraCollection, Region} from '@/entities/Entities';
import Tabs from '@components/structure/navigation/Tabs.vue';
import Action from '@utils/toolbar/Action.vue';
import CampaignModal from '@pages/campaigns/campaignGroup/CampaignModal.vue';
import {useDelete} from '@hooks/api';
import Export from '@utils/toolbar/panes/Export.vue';
import CampaignDesignFileModal from '@pages/campaigns/campaignGroup/CampaignDesignFileModal.vue';
import {formatTimeDurationFromSeconds} from '@/utils/helpers';
import {useQueryRef} from '@/hooks/url';
import QrModal from '@pages/campaigns/QrModal.vue';

import CampaignGroupModal from '@pages/campaigns/CampaignGroupModal.vue';
import {useTableFields} from '@hooks/table-configuration';
import globalFilterKey from '@utils/globalFilter/injects';

interface Props {
  modelValue: CampaignGroup;
}

const props = defineProps<Props>();

const emit = defineEmits(['reload']);

const globalFilter = inject(globalFilterKey) as unknown as Ref<{ regions: {label: string, value: string}[] }>;

const campaignGroupNeedsLoading = computed(() => {
  return !props.modelValue;
});

const page = useQueryRef('page', 1);
const perPage = useQueryRef('perPage', 10);

const campaignGroupModalOpen = ref(false);

const campaignModalOpen = ref(false);
const editCampaign = ref<Campaign | undefined>(undefined);
const campaignDesignFileModalOpen = ref(false);
const currentCampaignGroup = ref();
const qrModalOpen = ref(false);

const sortKey = useQueryRef<string | undefined>('sortKey', 'createdAt');
const sortDirection = useQueryRef<'ASC' | 'DESC' | undefined>('sortDirection', 'DESC');

const { t } = useI18n();

const [deleteCampaign, { loading: deleting }] = useDelete(t('prompt.deleteCampaignModal.description'));

const csvTitle = computed(() => {
  return t('csv.title.campaignsFromGroup', {
    group: props.modelValue?.name,
  });
});

const filter = useQueryRef('filter', {});
const fixedFilter = computed(() => ({
  group: props.modelValue?.id ?? '',
  'inArray[region]': globalFilter?.value?.regions,
}));
const totalFilter = computed(() => {
  return { ...fixedFilter.value, ...filter.value };
});

const providerParams = reactive({
  sort: {
    sortKey,
    sortDirection,
  },
  filter: totalFilter,
  pagination: {
    page,
    perPage,
  },
});

const actions = [
  {
    title: t('pages.campaignList.actions.edit'),
    action: (item: Campaign) => {
      editCampaign.value = { ...item };
      campaignModalOpen.value = true;
    },
  },
  {
    title: t('pages.campaignList.actions.delete'),
    action: async (item: Campaign) => {
      if (await deleteCampaign(`/campaigns/${item.id}`)) {
        // eslint-disable-next-line no-use-before-define
        await refetch();
      }
    },
  },
  {
    title: t('pages.campaignList.actions.designFile'),
    action: async (item: Campaign) => {
      editCampaign.value = { ...item };
      campaignDesignFileModalOpen.value = true;
    },
    condition: (item: Campaign) => (item.designPreviews?.length ?? 0) > 0,
  },
  {
    title: t('pages.campaignGroupList.actions.qrCode'),
    action: (item: Campaign) => {
      currentCampaignGroup.value = props.modelValue;
      qrModalOpen.value = true;
    },
  },
];

type ColumnFilter = Partial<Filter> & Omit<Filter, 'key'>;
type ColumnDefinition = Column & { filter?: ColumnFilter | ColumnFilter[] } & { group: string };

const uniqueRegions = computed(() => {
  const regions: Record<string, Region> = {};
  props.modelValue?.regions?.forEach((region) => {
    regions[region.id] = region;
  });
  return Object.values(regions);
});

const columns: ColumnDefinition[] = [
  {
    key: 'id',
    title: t('id'),
    sortable: true,
    group: t('pages.campaignList.fieldGroups.general'),
    class: 'whitespace-nowrap',
  },
  {
    key: 'region.name',
    title: t('campaign.region'),
    group: t('pages.campaignList.fieldGroups.general'),
    sortable: true,
    visible: true,
  },
  {
    key: 'timeStatus',
    csvTranslations: {
      ongoing: t('campaign.timeStatus.ongoing'),
      future: t('campaign.timeStatus.future'),
      past: t('campaign.timeStatus.past'),
    },
    title: t('campaign.timeStatus.DEFAULT'),
    group: t('pages.campaignList.fieldGroups.general'),
    sortable: false,
    visible: true,
    computed: (c: Campaign) => {
      const now = new Date();
      const startDate = new Date(c.actualStartDate);
      const endDate = new Date(c.actualEndDate);
      if(startDate <= now && endDate >= now) {
        return t('campaign.timeStatus.ongoing');
      } else if(startDate > now) {
        return t('campaign.timeStatus.future');
      } else {
        return t('campaign.timeStatus.past');
      }
    },
    filter: {
      type: 'dropdown',
      group: t('pages.campaignList.filterGroups.general'),
      options: {
        options: [
          {
            label: t('campaign.timeStatus.past'),
            value: 'past',
          },
          {
            label: t('campaign.timeStatus.ongoing'),
            value: 'ongoing',
          },
          {
            label: t('campaign.timeStatus.future'),
            value: 'future',
          },
        ],
        label: t('campaign.timeStatus.DEFAULT'),
      },
    },
  },
  {
    key: 'actualStartDate',
    title: t('campaign.startDate'),
    group: t('pages.campaignList.fieldGroups.general'),
    sortable: true,
    visible: true,
    formatter: (v: string) => v ? format(new Date(v), 'dd.MM.yyyy') : '',
    filter: {
      type: 'datetime',
      group: t('pages.campaignList.filterGroups.general'),
      options: {
        label: t('campaign.startDate'),
      },
    },
  },
  {
    key: 'actualEndDate',
    title: t('campaign.endDate'),
    group: t('pages.campaignList.fieldGroups.general'),
    sortable: true,
    visible: true,
    formatter: (v: string) => v ? format(new Date(v), 'dd.MM.yyyy') : '',
    filter: {
      type: 'datetime',
      group: t('pages.campaignList.filterGroups.general'),
      options: {
        label: t('campaign.endDate'),
      },
    },
  },
  {
    key: 'driversCount',
    title: t('campaign.bookingNumbers'),
    group: t('pages.campaignList.fieldGroups.general'),
    sortable: true,
    visible: true,
    computed: (c: Campaign) => `${c.bookingNumbers?.booked} / ${c.bookingNumbers?.slots}`,
    filter: {
      type: 'range',
      group: t('pages.campaignList.filterGroups.general'),
      options: {
        label: t('campaign.driversCount'),
      },
    },
  },
  {
    key: 'numOfDriversWithNoticeComment',
    title: t('campaign.numOfDriversWithNoticeComment'),
    group: t('pages.campaignList.fieldGroups.statistics'),
    sortable: true,
    visible: true,
    filter: {
      type: 'range',
      group: t('pages.campaignList.filterGroups.statistics'),
      options: {
        label: t('campaign.numOfDriversWithNoticeComment'),
      },
    },
  },
  {
    key: 'numOfDriversWithBox',
    title: t('campaign.numOfDriversWithBox'),
    group: t('pages.campaignList.fieldGroups.statistics'),
    sortable: true,
    visible: true,
    filter: {
      type: 'range',
      group: t('pages.campaignList.filterGroups.statistics'),
      options: {
        label: t('campaign.numOfDriversWithBox'),
      },
    },
  },
  {
    key: 'numOfFreeSlotsInRegion',
    title: t('campaign.numOfFreeSlotsInRegion'),
    group: t('pages.campaignList.fieldGroups.statistics'),
    sortable: false,
    visible: true,
  },
  {
    key: 'trackedDistance',
    title: t('campaign.trackedDistance'),
    group: t('pages.campaignList.fieldGroups.statistics'),
    sortable: true,
    visible: true,
    unit: 'km',
    formatter: (v: number) => `${(v / 1000).toFixed(2)} Km`,
    filter: {
      type: 'range',
      group: t('pages.campaignList.filterGroups.statistics'),
      options: {
        icon: <span>km</span>,
        label: t('campaign.trackedDistance'),
        transformOut: (v: number) => v * 1000,
        transformIn: (v: number) => v / 1000,
      },
    },
  },
  {
    key: 'trackedTime',
    title: t('campaign.trackedTime'),
    group: t('pages.campaignList.fieldGroups.statistics'),
    sortable: true,
    visible: true,
    formatter: (v: number) => formatTimeDurationFromSeconds(v),
    filter: {
      type: 'duration',
      group: t('pages.campaignList.filterGroups.statistics'),
      options: {
        placeholderFrom: 'hh:mm:ss',
        placeholderTo: 'hh:mm:ss',
        label: t('campaign.trackedTime'),
      },
    },
  },
  {
    key: 'kcal',
    title: t('driver.kcal'),
    group: t('pages.campaignList.fieldGroups.statistics'),
    sortable: true,
    visible: true,
    unit: 'kcal',
    formatter: (v: number) => (v ? v.toFixed(2) : 0) + ' kcal',
    filter: {
      type: 'range',
      group: t('pages.campaignList.filterGroups.statistics'),
      options: {
        icon: <span>kcal</span>,
        label: t('driver.kcal'),
      },
    },
  },
  {
    key: 'co2',
    title: t('driver.co2'),
    group: t('pages.campaignList.fieldGroups.statistics'),
    sortable: true,
    visible: true,
    unit: 'kg',
    formatter: (v: number) => (v ? v.toFixed(2) : 0) + ' kg',
    filter: {
      type: 'range',
      group: t('pages.campaignList.filterGroups.statistics'),
      options: {
        icon: <span>kg</span>,
        label: t('driver.co2'),
      },
    },
  },
  {
    key: 'numOfTours',
    title: t('campaign.numOfTours'),
    group: t('pages.campaignList.fieldGroups.statistics'),
    sortable: true,
    visible: true,
    filter: {
      type: 'range',
      group: t('pages.campaignList.filterGroups.statistics'),
      options: {
        label: t('campaign.numOfTours'),
      },
    },
  },
  {
    key: 'numOfTopPhotos',
    title: t('campaign.numOfTopPhotos'),
    group: t('pages.campaignList.fieldGroups.statistics'),
    sortable: true,
    visible: true,
    filter: {
      type: 'range',
      group: t('pages.campaignList.filterGroups.statistics'),
      options: {
        label: t('campaign.numOfTopPhotos'),
      },
    },
  },
  {
    key: 'avgSpeed',
    title: t('campaign.avgSpeed'),
    group: t('pages.campaignList.fieldGroups.statistics'),
    sortable: true,
    visible: true,
    unit: 'km/h',
    formatter: (v: number) => `${(v * 3.6).toFixed(2)} Km/h`,
    filter: {
      type: 'range',
      group: t('pages.campaignList.filterGroups.statistics'),
      options: {
        icon: <span>km/h</span>,
        label: t('campaign.avgSpeed'),
        transformOut: (v: number) => v / 3.6,
        transformIn: (v: number) => v * 3.6,
      },
    },
  },
  {
    key: 'active',
    title: t('campaign.active'),
    group: t('pages.campaignList.fieldGroups.general'),
    sortable: true,
    visible: true,
    formatter: (v: boolean) => v ? t('yes') : t('no'),
    filter: {
      type: 'bool',
      group: t('pages.campaignList.filterGroups.general'),
      options: {
        label: t('campaign.active'),
      },
    },
  },
  {
    key: 'createdAt',
    title: t('createdAt'),
    group: t('pages.campaignList.fieldGroups.general'),
    sortable: true,
    visible: true,
    formatter: (v: string) => (v ? format(new Date(v), 'dd.MM.yyyy - HH:mm:ss') : ''),
    filter: {
      type: 'datetime',
      group: t('pages.campaignList.filterGroups.general'),
      options: {
        label: t('createdAt'),
      },
    },
  },
];

const visibleColumns = useTableFields(
    columns,
);

const {
  data: items,
  loading,
  refetch,
} = useProvider<HydraCollection<Campaign>>('/campaigns', providerParams, {
  disabled: campaignGroupNeedsLoading,
});

const records = computed(() => unref(items)?.['hydra:member']);
const total = computed(() => unref(items)?.['hydra:totalItems']);
const filters = computed(() =>
  columns.flatMap((col): Filter[] => {
    if (!col.filter) return [];
    if (Array.isArray(col.filter))
      return col.filter.map((f) => ({
        key: col.key,
        ...f,
      }));
    return [{ key: col.key, ...col.filter }];
  })
);

const totalLoading = computed(() => loading.value || deleting.value);

const editCampaignGroup = ref();
const openCampaignGroupModal = () => {
  editCampaignGroup.value = JSON.parse(JSON.stringify(props.modelValue));
  campaignGroupModalOpen.value = true;
};
</script>

<style lang="scss" scoped>
button {
  @apply block;
}

.table {
  @apply flex flex-col;
}
</style>

